'use client';

import { useQueryClient } from '@tanstack/react-query';
import { WebSocket } from 'partysocket';
import type { ProtocolsProvider } from 'partysocket/ws';
import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import superjson from 'superjson';

import {
  type DatasetKey,
  type DatasetRequestWebSocketMessage,
  type Datasets,
  type UserWebSocketResponseMessage,
} from '@infinex/types';

import { datasetQueryKeys } from './hooks/useDataset';
import { type TransactionalEventHandler, UserContext } from './UserContext';

import {
  type DatasetExtraTabs,
  type DebugDatasetInfo,
  type DebuggerData,
  DatasetsDebugger,
  useUserDebug,
} from '../debugger';
import { useTRPCClient } from '../main-service-client';
import {
  type OnSessionChangeCallback,
  useOnSessionChange,
  useSession,
} from '../session';

export { ProtocolsProvider };
export type { TransactionalEventHandler };

/** Default fallback polling interval: 60 seconds */
const DEFAULT_FALLBACK_POLL_INTERVAL_MS = 60 * 1000;

export type UserProviderProps = {
  children: ReactNode;
  wsUrl: string;
  extraDatasetTabs?: DatasetExtraTabs;
  customHeaderActions?: ReactNode;
  /**
   * Interval in milliseconds for fallback TRPC polling when WebSocket isn't
   * delivering updates. Set to `false` to disable fallback polling.
   * @default 60000 (60 seconds)
   */
  fallbackPollInterval?: number | false;
  protocolsProvider?: ProtocolsProvider;

  /**
   * @deprecated [Dicey] Not intended behaviour for the UserProvider.
   * If your app needs, use useOnSessionChange hook to implement it.
   */
  reloadOnSessionChange?: boolean;
};

/**
 * Subscriber callback invoked when WebSocket receives data for a dataset key.
 */
type DatasetSubscriber<K extends DatasetKey = DatasetKey> = {
  key: K;
  callback: (data: Datasets[K]) => void;
};

/**
 * UserProvider manages WebSocket connections and provides dataset streaming.
 *
 * Architecture:
 * - streamDataset() returns an AsyncIterable for React Query's streamedQuery
 * - Initial data: WebSocket request → wait → fallback to TRPC if timeout
 * - Real-time updates: WebSocket pushes → yielded through the stream
 * - React Query manages caching, persistence, and lifecycle
 */
export const UserProvider = ({
  children,
  extraDatasetTabs,
  customHeaderActions,
  fallbackPollInterval = DEFAULT_FALLBACK_POLL_INTERVAL_MS,
  protocolsProvider,
  reloadOnSessionChange = false,
  wsUrl,
}: UserProviderProps) => {
  const { logDebugMessage } = useUserDebug();

  // ═══════════════════════════════════════════════════════════════════════════
  // WebSocket State
  // ═══════════════════════════════════════════════════════════════════════════
  //
  // We need two pieces of state to manage the WebSocket:
  //
  // 1. ws - The actual WebSocket instance. Used to send messages.
  //    - Created once per session in the useEffect below
  //    - Persists across component re-renders and dataset invalidations
  //    - Closed only on logout or unmount
  //
  // 2. wsReady - A deferred promise that resolves when WebSocket opens.
  //    - Needed because WebSocket connection is async (open event fires later)
  //    - Allows streamDataset() to await wsReady.current.promise before sending
  //    - Contains both the promise AND its resolve function in one object
  //    - Resolved in onWebSocketOpen, cleared on close/cleanup
  const ws = useRef<WebSocket | null>(null);
  const wsReady = useRef<{
    promise: Promise<void>;
    resolve: () => void;
  } | null>(null);

  // ═══════════════════════════════════════════════════════════════════════════
  // Dataset Subscribers
  // ═══════════════════════════════════════════════════════════════════════════
  //
  // Each active stream (from useDataset) registers a subscriber callback.
  // When WebSocket receives data for a key, all subscribers for that key are notified.
  // This allows multiple components to share the same WebSocket connection.
  // ═══════════════════════════════════════════════════════════════════════════
  const datasetSubscribers = useRef<Map<DatasetKey, Set<DatasetSubscriber>>>(
    new Map()
  );

  /** Tracks how each dataset received its most recent data (for debugger) */
  const datasetSources = useRef<Map<DatasetKey, 'websocket' | 'trpc'>>(
    new Map()
  );

  /**
   * Tracks when each dataset's data last changed (for debugger "time ago").
   * We can't rely on React Query's dataUpdatedAt because streamedQuery only
   * sets it on a new fetch, not when a chunk arrives on an existing stream.
   * Updated in streamDataset whenever data arrives via WS or tRPC.
   */
  const datasetLastChanged = useRef<Map<DatasetKey, number>>(new Map());

  /** Handlers for transactional events (deposits, withdrawals, etc.) */
  const eventHandlers = useRef<Set<TransactionalEventHandler>>(new Set());

  const queryClient = useQueryClient();
  const apiClient = useTRPCClient();
  const { session } = useSession();

  // ═══════════════════════════════════════════════════════════════════════════
  // Session Change Handling
  // ═══════════════════════════════════════════════════════════════════════════
  const onSessionChangeCallback = useCallback<OnSessionChangeCallback>(
    (previous, current) => {
      if (current && previous) {
        // User switched accounts - clear cache and reload to avoid stale data
        queryClient.removeQueries({ queryKey: datasetQueryKeys.all });

        if (reloadOnSessionChange) {
          top?.location.reload();
        }
      }
    },
    [queryClient]
  );

  useOnSessionChange(onSessionChangeCallback);

  // ═══════════════════════════════════════════════════════════════════════════
  // WebSocket Message Handlers
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Handle incoming WebSocket messages.
   * Routes dataset updates to subscribers and transactional events to handlers.
   */
  const onWebSocketMessage = useCallback(
    (event: MessageEvent) => {
      logDebugMessage({ type: 'receive', message: event.data });

      const parsed = superjson.parse<UserWebSocketResponseMessage>(event.data);

      switch (parsed.type) {
        case 'dataset': {
          // Notify all subscribers waiting for this dataset
          const subscribers = datasetSubscribers.current.get(parsed.key);
          if (subscribers) {
            subscribers.forEach((sub) => {
              try {
                sub.callback(parsed.data as Datasets[typeof parsed.key]);
              } catch (error) {
                console.error(
                  `Error in dataset subscriber for ${parsed.key}:`,
                  error
                );
              }
            });
          }
          break;
        }

        case 'transactionalEvent': {
          // Call all subscribed handlers
          eventHandlers.current.forEach((handler) => {
            try {
              handler(parsed.event);
            } catch (error) {
              console.error('Error in transactional event handler:', error);
            }
          });
          break;
        }

        default:
          console.warn('Unknown WebSocket message:', parsed);
      }
    },
    [logDebugMessage]
  );

  /**
   * Handle WebSocket open event.
   * Resolves the connection promise so waiters can proceed.
   */
  const onWebSocketOpen = useCallback(() => {
    logDebugMessage({ type: 'status', message: 'WebSocket connected' });

    // Resolve the deferred promise - any code awaiting it will now continue
    wsReady.current?.resolve();
    wsReady.current = null;
  }, [logDebugMessage]);

  /**
   * Handle WebSocket close event.
   * Clears connection state so new connections can be established.
   */
  const onWebSocketClose = useCallback(() => {
    logDebugMessage({ type: 'status', message: 'WebSocket disconnected' });
    wsReady.current = null;
  }, [logDebugMessage]);

  // ═══════════════════════════════════════════════════════════════════════════
  // WebSocket Connection Lifecycle
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    if (!session?.sessionId) {
      logDebugMessage({
        type: 'status',
        message: 'No session, skipping WebSocket',
      });
      return;
    }

    logDebugMessage({ type: 'status', message: 'Connecting WebSocket...' });

    let resolve: () => void;
    const promise = new Promise<void>((r) => {
      resolve = r;
    });
    wsReady.current = { promise, resolve: resolve! };

    // Create WebSocket connection
    ws.current = connectWebSocket(wsUrl, protocolsProvider || undefined);
    ws.current.addEventListener('message', onWebSocketMessage);
    ws.current.addEventListener('open', onWebSocketOpen);
    ws.current.addEventListener('close', onWebSocketClose);

    // Cleanup on unmount or session change
    return () => {
      if (!ws.current) return;

      logDebugMessage({ type: 'status', message: 'Cleaning up WebSocket' });

      ws.current.removeEventListener('message', onWebSocketMessage);
      ws.current.removeEventListener('open', onWebSocketOpen);
      ws.current.removeEventListener('close', onWebSocketClose);

      if (ws.current.readyState === WebSocket.OPEN) {
        ws.current.close(1000, 'UserProvider unmounting');
      }

      // Clear all state
      ws.current = null;
      wsReady.current = null;
      datasetSubscribers.current.clear();
      datasetSources.current.clear();
    };
  }, [
    session?.sessionId,
    wsUrl,
    protocolsProvider,
    onWebSocketMessage,
    onWebSocketOpen,
    onWebSocketClose,
    logDebugMessage,
  ]);

  // ═══════════════════════════════════════════════════════════════════════════
  // streamDataset - The queryFn for React Query's streamedQuery
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Stream a dataset. Returns an AsyncIterable that:
   * 1. Subscribes to WebSocket updates for this key
   * 2. Waits for WebSocket connection (with timeout)
   * 3. Sends dataset request via WebSocket
   * 4. Yields initial data (or falls back to TRPC on timeout)
   * 5. Continues yielding real-time updates until abort signal
   */
  const streamDataset = useCallback(
    async <K extends DatasetKey>(
      key: K,
      timeoutMs: number,
      signal: AbortSignal
    ): Promise<AsyncIterable<Datasets[K]>> => {
      logDebugMessage({
        type: 'status',
        message: `[streamDataset] Starting stream for ${key}`,
      });

      async function* datasetGenerator(): AsyncGenerator<Datasets[K]> {
        // ─────────────────────────────────────────────────────────────────────
        // Setup: Create a queue and subscriber for this stream
        // ─────────────────────────────────────────────────────────────────────

        /** Queue of data received from WebSocket */
        const queue: Datasets[K][] = [];

        /** Resolve function to wake up waitForData() when data arrives */
        let resolveWait: (() => void) | null = null;

        /** Subscriber callback - pushes data to queue and wakes waiter */
        const subscriber: DatasetSubscriber<K> = {
          key,
          callback: (data) => {
            queue.push(data);
            if (resolveWait) {
              resolveWait();
              resolveWait = null;
            }
          },
        };

        // Register subscriber to receive WebSocket messages for this key
        // Double cast needed: DatasetSubscriber<K> → unknown → DatasetSubscriber
        // This is safe because subscriber.key matches the map key we're adding to
        if (!datasetSubscribers.current.has(key)) {
          datasetSubscribers.current.set(key, new Set());
        }
        datasetSubscribers.current
          .get(key)!
          .add(subscriber as unknown as DatasetSubscriber);

        // ─────────────────────────────────────────────────────────────────────
        // Helpers
        // ─────────────────────────────────────────────────────────────────────

        /** Returns a Promise that resolves when data is available in queue */
        const waitForData = () =>
          new Promise<void>((resolve) => {
            if (queue.length > 0) {
              resolve();
            } else {
              resolveWait = resolve;
            }
          });

        /** Fallback: fetch via TRPC if WebSocket times out */
        const fetchViaTrpc = async (): Promise<Datasets[K]> => {
          logDebugMessage({
            type: 'status',
            message: `[streamDataset] Falling back to TRPC for ${key}`,
          });
          const result = await apiClient.user.getDataset.query({ key });
          datasetSources.current.set(key, 'trpc');
          datasetLastChanged.current.set(key, Date.now());
          return result as Datasets[K];
        };

        /** Send dataset request via WebSocket */
        const sendRequest = () => {
          const requestMessage: DatasetRequestWebSocketMessage = {
            type: 'dataset',
            key,
          };
          const msg = superjson.stringify(requestMessage);
          logDebugMessage({ type: 'send', message: msg });

          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(msg);
          }
        };

        // ─────────────────────────────────────────────────────────────────────
        // Main Flow
        // ─────────────────────────────────────────────────────────────────────

        try {
          let initialData: Datasets[K] | null = null;

          // Case 1: No WebSocket exists yet
          // This can happen due to a race condition: both useDataset and the
          // WebSocket useEffect trigger when session becomes available. React
          // Query might call streamDataset() before the effect creates the
          // WebSocket. In this case, wsReady.current is also null so there's
          // nothing to await - we fall back to TRPC immediately.
          //
          // Alternatively we could create a deferred promise upfront and queue
          // requests until the WebSocket connects (with a timeout). We chose
          // the simpler TRPC fallback since it's reliable and subsequent
          // real-time updates still flow through WebSocket once it connects.
          if (!ws.current) {
            initialData = await fetchViaTrpc();
          } else {
            // Case 2: WebSocket exists but might not be connected yet
            // The WebSocket was created but the 'open' event hasn't fired.
            // We await wsReady.promise which resolves when onWebSocketOpen runs.
            const isConnected = ws.current.readyState === WebSocket.OPEN;

            if (!isConnected && wsReady.current) {
              const connectionResult = await Promise.race([
                wsReady.current.promise.then(() => 'connected' as const),
                new Promise<'timeout'>((resolve) =>
                  setTimeout(() => resolve('timeout'), timeoutMs)
                ),
              ]);

              if (connectionResult === 'timeout') {
                initialData = await fetchViaTrpc();
              }
            }

            // Case 3: WebSocket is connected - send request and wait for response
            if (!initialData) {
              sendRequest();

              const dataResult = await Promise.race([
                waitForData().then(() => queue.shift()!),
                new Promise<'timeout'>((resolve) =>
                  setTimeout(() => resolve('timeout'), timeoutMs)
                ),
              ]);

              if (dataResult === 'timeout') {
                initialData = await fetchViaTrpc();
              } else {
                initialData = dataResult;
                datasetSources.current.set(key, 'websocket');
                datasetLastChanged.current.set(key, Date.now());
              }
            }
          }

          // Yield initial data to React Query
          if (initialData) {
            yield initialData;
          }

          // ───────────────────────────────────────────────────────────────────
          // Real-time Updates Loop
          // ───────────────────────────────────────────────────────────────────
          // Keep yielding updates until the stream is aborted (component unmounts
          // or query is invalidated). WebSocket pushes arrive via the subscriber
          // callback and are queued for yielding here.
          //
          // Fallback polling: Only poll via TRPC when WebSocket connection is
          // down. If WebSocket is connected, we trust it to deliver updates.

          while (!signal.aborted) {
            // Wait for either: data arrives, abort signal, OR fallback timeout
            let onAbort: (() => void) | null = null;
            const abortPromise = new Promise<'abort'>((resolve) => {
              onAbort = () => resolve('abort');
              signal.addEventListener('abort', onAbort, { once: true });
            });

            // Build race conditions: always wait for data and abort
            const racePromises: Promise<'data' | 'abort' | 'fallback'>[] = [
              waitForData().then(() => 'data' as const),
              abortPromise,
            ];

            // Only add fallback polling if WebSocket is NOT connected
            const isWebSocketConnected =
              ws.current?.readyState === WebSocket.OPEN;

            if (fallbackPollInterval !== false && !isWebSocketConnected) {
              racePromises.push(
                new Promise<'fallback'>((resolve) =>
                  setTimeout(() => resolve('fallback'), fallbackPollInterval)
                )
              );
            }

            const result = await Promise.race(racePromises);

            // Clean up abort listener if it didn't win
            if (onAbort && !signal.aborted) {
              signal.removeEventListener('abort', onAbort);
            }

            if (signal.aborted || result === 'abort') break;

            if (result === 'fallback') {
              // WebSocket is down - fall back to TRPC polling
              logDebugMessage({
                type: 'status',
                message: `[streamDataset] WebSocket down for ${key}, polling via TRPC`,
              });
              const polledData = await fetchViaTrpc();
              yield polledData;
            } else {
              // WebSocket delivered data - yield all queued items
              datasetSources.current.set(key, 'websocket');
              datasetLastChanged.current.set(key, Date.now());
              while (queue.length > 0) {
                yield queue.shift()!;
              }
            }
          }
        } finally {
          // ───────────────────────────────────────────────────────────────────
          // Cleanup: Remove subscriber when stream ends
          // ───────────────────────────────────────────────────────────────────
          logDebugMessage({
            type: 'status',
            message: `[streamDataset] Ending stream for ${key}`,
          });

          const subs = datasetSubscribers.current.get(key);
          if (subs) {
            subs.delete(subscriber as unknown as DatasetSubscriber);
            if (subs.size === 0) {
              datasetSubscribers.current.delete(key);
            }
          }
        }
      }

      return datasetGenerator();
    },
    [logDebugMessage, apiClient, fallbackPollInterval]
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // Transactional Events Subscription
  // ═══════════════════════════════════════════════════════════════════════════

  const subscribeToTransactionalEvents = useCallback(
    (handler: TransactionalEventHandler) => {
      eventHandlers.current.add(handler);
      return () => {
        eventHandlers.current.delete(handler);
      };
    },
    []
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // Debugger Support
  // ═══════════════════════════════════════════════════════════════════════════

  const getDatasetsForDebugger = useCallback((): DebuggerData => {
    const cachedQueries = queryClient.getQueriesData<unknown>({
      queryKey: datasetQueryKeys.all,
    });

    const datasets: Record<string, DebugDatasetInfo> = {};
    cachedQueries.forEach(([queryKey, data]) => {
      const datasetKey = queryKey[1] as DatasetKey;
      if (datasetKey) {
        const queryState = queryClient.getQueryState(queryKey);
        const hasData = data !== undefined;

        let status: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';
        if (hasData) {
          status = 'loaded';
        } else if (queryState?.fetchStatus === 'fetching') {
          status = 'loading';
        } else if (queryState?.status === 'error') {
          status = 'error';
        }

        datasets[datasetKey] = {
          status,
          data: hasData ? data : undefined,
          dataUpdatedAt: datasetLastChanged.current.get(datasetKey),
          fetchStatus: queryState?.fetchStatus ?? 'idle',
          source: datasetSources.current.get(datasetKey),
        };
      }
    });

    const wsStatus: DebuggerData['wsStatus'] =
      ws.current?.readyState === WebSocket.OPEN
        ? 'connected'
        : ws.current?.readyState === WebSocket.CONNECTING
          ? 'connecting'
          : 'disconnected';

    const markDatasetUpdated = (key: DatasetKey) => {
      datasetLastChanged.current.set(key, Date.now());
    };

    return {
      datasets,
      wsStatus,
      fallbackPollIntervalMs: fallbackPollInterval,
      markDatasetUpdated,
    };
  }, [queryClient, fallbackPollInterval]);

  // ═══════════════════════════════════════════════════════════════════════════
  // Context Provider
  // ═══════════════════════════════════════════════════════════════════════════

  const contextValue = useMemo(
    () => ({
      streamDataset,
      subscribeToTransactionalEvents,
    }),
    [streamDataset, subscribeToTransactionalEvents]
  );

  return (
    <UserContext.Provider value={contextValue}>
      <DatasetsDebugger
        getDebuggerData={getDatasetsForDebugger}
        extraDatasetTabs={extraDatasetTabs}
        customHeaderActions={customHeaderActions}
      />
      {children}
    </UserContext.Provider>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════════

function connectWebSocket(wsUrl: string, protocols?: ProtocolsProvider) {
  const ws = new WebSocket(wsUrl, protocols);
  ws.addEventListener('error', (error) => {
    console.error('[WebSocket] error:', error);
  });
  return ws;
}
